import type { FUSportsMatchEvent } from "fansunited-data-layer";

/**
 * Player events summary for lineup display
 */
export interface PlayerEvents {
    goals: number;
    assists: number;
    yellowCard: boolean;
    redCard: boolean;
    substitutedOut: boolean;
    substitutedIn: boolean;
}

/**
 * Simple player info for substitution display
 */
export interface SubstitutionPlayer {
    id: string;
    name: string;
}

/**
 * Substitution event data for display
 */
export interface SubstitutionData {
    minute: number;
    injuryMinute?: number;
    playerOut: SubstitutionPlayer;
    playerIn: SubstitutionPlayer;
    isHome: boolean;
}

/**
 * Map of player ID to their events
 */
export type PlayerEventsMap = Map<string, PlayerEvents>;

/**
 * Goal event types
 */
const GOAL_TYPES = ["GOAL", "PENALTY_GOAL", "PENALTY_SHOOTOUT_SCORED"];

/**
 * Yellow card event types
 */
const YELLOW_CARD_TYPES = ["YELLOW_CARD"];

/**
 * Red card event types (including second yellow)
 */
const RED_CARD_TYPES = ["RED_CARD", "YELLOW_RED_CARD"];

/**
 * Compute player events from match timeline
 */
export function computePlayerEvents(events: FUSportsMatchEvent[]): PlayerEventsMap {
    const map: PlayerEventsMap = new Map();

    const getOrCreate = (playerId: string): PlayerEvents => {
        if (!map.has(playerId)) {
            map.set(playerId, {
                goals: 0,
                assists: 0,
                yellowCard: false,
                redCard: false,
                substitutedOut: false,
                substitutedIn: false,
            });
        }
        return map.get(playerId)!;
    };

    for (const event of events) {
        const type = event.type;
        const primaryPlayerId = event.primaryPlayer?.id;
        const secondaryPlayerId = event.secondaryPlayer?.id;

        // Goals
        if (GOAL_TYPES.includes(type) && primaryPlayerId) {
            getOrCreate(primaryPlayerId).goals++;
            // Secondary player is the assister
            if (secondaryPlayerId) {
                getOrCreate(secondaryPlayerId).assists++;
            }
        }

        // Yellow cards
        if (YELLOW_CARD_TYPES.includes(type) && primaryPlayerId) {
            getOrCreate(primaryPlayerId).yellowCard = true;
        }

        // Red cards
        if (RED_CARD_TYPES.includes(type) && primaryPlayerId) {
            getOrCreate(primaryPlayerId).redCard = true;
            // YELLOW_RED_CARD means they also had a yellow
            if (type === "YELLOW_RED_CARD") {
                getOrCreate(primaryPlayerId).yellowCard = true;
            }
        }

        // Substitutions
        if (type === "SUBSTITUTION") {
            // Primary player is substituted OUT
            if (primaryPlayerId) {
                getOrCreate(primaryPlayerId).substitutedOut = true;
            }
            // Secondary player is substituted IN
            if (secondaryPlayerId) {
                getOrCreate(secondaryPlayerId).substitutedIn = true;
            }
        }
    }

    return map;
}

/**
 * Extract substitution events from match timeline
 * Returns arrays of substitutions for home and away teams
 */
export function extractSubstitutions(events: FUSportsMatchEvent[]): {
    home: SubstitutionData[];
    away: SubstitutionData[];
} {
    const home: SubstitutionData[] = [];
    const away: SubstitutionData[] = [];

    for (const event of events) {
        if (event.type !== "SUBSTITUTION") continue;

        const playerOut = event.primaryPlayer;
        const playerIn = event.secondaryPlayer;

        // Both players must exist for a valid substitution display
        if (!playerOut?.id || !playerOut?.name || !playerIn?.id || !playerIn?.name) continue;

        // competitorPosition "ONE" = home team, "TWO" = away team
        const isHome = event.competitorPosition === "ONE";

        const sub: SubstitutionData = {
            minute: event.minute || 0,
            injuryMinute: event.injuryMinute,
            playerOut: { id: playerOut.id, name: playerOut.name },
            playerIn: { id: playerIn.id, name: playerIn.name },
            isHome,
        };

        if (isHome) {
            home.push(sub);
        } else {
            away.push(sub);
        }
    }

    // Sort by minute
    home.sort((a, b) => a.minute - b.minute);
    away.sort((a, b) => a.minute - b.minute);

    return { home, away };
}
